import DynamicFormRenderer from '@aims/shared/report-schedules/DynamicFormRenderer';
import {
  ScheduleEntryStatuses,
  ScheduleFrequencies,
  reportScheduleQuery,
} from '@aims/shared/report-schedules/constants';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Descriptions,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import { allReportScheduleTemplatesQuery } from '../report-schedule-templates/constants';

const { Title } = Typography;

function PreviewScheduleEntryPage() {
  const { scheduleId, entryId } = useParams();
  const history = useHistory();

  const { data: scheduleData, loading: scheduleLoading } = useQuery(
    reportScheduleQuery,
    {
      variables: { _id: scheduleId },
      skip: !scheduleId,
      fetchPolicy: 'network-only',
    },
  );

  const entry = useMemo(() => {
    const entries = scheduleData?.reportSchedule?.entries || [];
    return entries.find((e) => e.entryId === entryId) || null;
  }, [scheduleData, entryId]);

  const { data: templatesData, loading: templatesLoading } = useQuery(
    allReportScheduleTemplatesQuery,
    {
      variables: { first: 200, filters: {} },
      fetchPolicy: 'cache-and-network',
    },
  );

  const template = useMemo(() => {
    if (!entry?.templateId || !templatesData) return null;
    const edges = templatesData?.allReportScheduleTemplates?.edges || [];
    return edges.map((e) => e.node).find((t) => t._id === entry.templateId);
  }, [entry, templatesData]);

  const loading = scheduleLoading || templatesLoading;

  return (
    <CSPage containerStyle={{ maxWidth: 800 }} title="Preview Report Entry">
      <CSPageHeader
        title="Preview Report Entry"
        backActions={[
          <Button
            key="back"
            type="text"
            onClick={() => history.goBack()}
            icon={<ArrowLeftOutlined />}
          >
            Go back
          </Button>,
        ]}
      />
      {loading && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Skeleton active />
          <Skeleton active />
        </Space>
      )}
      {!loading && entry && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Descriptions title="Entry Details" bordered column={2}>
              <Descriptions.Item label="Label">{entry.label}</Descriptions.Item>
              <Descriptions.Item label="Frequency">
                <Tag
                  color={
                    ScheduleFrequencies[entry.frequency]?.color || 'default'
                  }
                >
                  {ScheduleFrequencies[entry.frequency]?.label ||
                    entry.frequency}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {entry.dueDate
                  ? dayjs(entry.dueDate).format('MMMM D, YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    ScheduleEntryStatuses[entry.status]?.color || 'default'
                  }
                >
                  {ScheduleEntryStatuses[entry.status]?.label || entry.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Template" span={2}>
                {template?.name || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {template && (
            <Card title="Report Form Preview">
              <DynamicFormRenderer
                formFields={template.formFields || []}
                formData={{}}
                readOnly={false}
                disabled
              />
            </Card>
          )}
        </>
      )}
    </CSPage>
  );
}

export default PreviewScheduleEntryPage;
